import { hash, produce, uuid } from "@banjoanton/utils";
import z from "zod/v4";
import {
    type ChessComRawGame,
    ChessComResultValues,
} from "../features/import/models/chess-com-models";
import { ChessService, PgnSummarySchema } from "../services/chess-service";
import { ChessColorsSchema } from "./base-model";
import { Position, PositionSchema } from "./position-model";
import { GameUtil } from "../utils/game-util";
import { TimeControlUtil } from "../utils/time-control-util";

export const ChessSimpleResultValues = ["win", "loss", "draw"] as const;
export type ChessSimpleResult = (typeof ChessSimpleResultValues)[number];

// directly from lichess api
export const ChessPlayerExternalAnalysisSchema = z.object({
    inaccuracy: z.number().optional(),
    mistake: z.number().optional(),
    blunder: z.number().optional(),
    averageCentipawnLoss: z.number().optional(),
    accuracy: z.number(),
});

export type ChessPlayerExternalAnalysis = z.infer<
    typeof ChessPlayerExternalAnalysisSchema
>;

export const ChessPlayerAnalysisSchema = z.object({
    inaccuracy: z.number(),
    mistake: z.number(),
    blunder: z.number(),
    excellent: z.number(),
    great: z.number(),
    good: z.number(),
    best: z.number(),
    miss: z.number(),
    brilliant: z.number(),
    averageCentipawnLoss: z.number(),
    accuracy: z.number(),
});

export type ChessPlayerAnalysis = z.infer<typeof ChessPlayerAnalysisSchema>;

export const ChessPlayerSchema = z.object({
    rating: z.number(),
    username: z.string(),
    result: z.enum(ChessComResultValues), // update this to not rely on chess.com
    simpleResult: z.enum(ChessSimpleResultValues),
    externalAnalysis: ChessPlayerExternalAnalysisSchema.optional(),
    analysis: ChessPlayerAnalysisSchema.optional(),
});

export type ChessPlayer = z.infer<typeof ChessPlayerSchema>;

export const ChessGameSource = ["chess.com", "lichess"] as const;
export type ChessGameSource = (typeof ChessGameSource)[number];
export const OpeningSchema = z.object({
    eco: z.string(),
    name: z.object({
        family: z.string(),
        variations: z.array(z.string()).optional(),
    }),
    ply: z.number(),
    version: z.string(),
});

export type Opening = z.infer<typeof OpeningSchema>;

const TimeSchema = z.object({
    control: z.string(),
    controlMs: z.number().optional(),
    class: z.string(),
});

export const ChessGameSchema = z
    .object({
        id: z.string(),
        slug: z.string(),
        url: z.string().optional(),
        pgn: z.string(),
        time: TimeSchema,
        endTimeUnix: z.number(),
        rated: z.boolean(),
        initialFen: z.string(),
        finalFen: z.string(),
        rules: z.string(),
        source: z.enum(ChessGameSource),
        white: ChessPlayerSchema,
        black: ChessPlayerSchema,
        player: ChessPlayerSchema,
        opponent: ChessPlayerSchema,
        externalId: z.string(),
        username: z.string(),
        playerResult: z.enum(ChessSimpleResultValues),
        playerColor: ChessColorsSchema,
        collectionId: z.string(),
        opening: OpeningSchema,
        positions: z.array(PositionSchema),
        hash: z.string(),
    })
    .extend(PgnSummarySchema.shape);

export type ChessGame = z.infer<typeof ChessGameSchema>;

/**
 * Generates a unique slug for a chess.com game.
 * The slug is created using the usernames of both players, the game end time, and the source.
 * This ensures that the slug is unique for each game, even if several users use the same slug across different clients.
 *
 * @param game - The raw game data from chess.com
 * @returns A unique slug string for the game
 */
const createSlugFromChessCom = (game: ChessComRawGame): string => {
    const source: ChessGameSource = "chess.com";
    const base = [
        source,
        game.white.username,
        game.black.username,
        game.end_time,
    ].join("-");

    return hash(base);
};

const from = (game: ChessGame) => game;

export type ChessGameWithoutHash = Omit<ChessGame, "hash">;

const createHash = (game: ChessGame | ChessGameWithoutHash): string => {
    const chessGameWithoutHash: ChessGameWithoutHash = structuredClone(game);
    if ("hash" in chessGameWithoutHash && chessGameWithoutHash.hash) {
        delete chessGameWithoutHash.hash;
    }

    return hash(chessGameWithoutHash);
};

export type FromChessComOptions = {
    game: ChessComRawGame;
    username: string;
    collectionId: string;
    opening: Opening;
};

const fromChessCom = ({
    game,
    username,
    collectionId,
    opening,
}: FromChessComOptions): ChessGame => {
    const { moves, comments, tags } = ChessService.parsePgn(game.pgn);
    const slug = createSlugFromChessCom(game);
    const userIsWhite =
        username.toLowerCase() === game.white.username.toLowerCase();

    const black: ChessPlayer = {
        username: game.black.username,
        rating: game.black.rating,
        result: game.black.result,
        simpleResult: GameUtil.getPlayerResultFromResult(game.black.result),
        externalAnalysis: game.accuracies
            ? { accuracy: game.accuracies.black }
            : undefined,
    };

    const white: ChessPlayer = {
        username: game.white.username,
        rating: game.white.rating,
        result: game.white.result,
        simpleResult: GameUtil.getPlayerResultFromResult(game.white.result),
        externalAnalysis: game.accuracies
            ? { accuracy: game.accuracies.white }
            : undefined,
    };

    const player = userIsWhite ? white : black;
    const opponent = userIsWhite ? black : white;
    const positions = Position.fromMoves(moves);
    const controlMs = TimeControlUtil.parseChessComTimeControlMs(
        game.time_control
    );
    const time = {
        control: game.time_control,
        controlMs,
        class: game.time_class,
    };

    const gameWithoutHash: ChessGameWithoutHash = {
        id: uuid(),
        slug,
        endTimeUnix: game.end_time,
        collectionId,
        initialFen: game.initial_setup,
        finalFen: game.fen,
        pgn: game.pgn,
        username,
        player,
        opponent,
        rated: game.rated,
        rules: game.rules,
        time,
        url: game.url,
        moves,
        positions,
        playerResult: player.simpleResult,
        playerColor: userIsWhite ? "w" : "b",
        comments,
        tags,
        source: "chess.com",
        externalId: game.uuid,
        black,
        white,
        opening,
    };

    const hash = createHash(gameWithoutHash);
    return { ...gameWithoutHash, hash };
};

const updateHash = (game: ChessGame): ChessGame =>
    produce(game, (draft) => {
        draft.hash = createHash(draft);
    });

export const ChessGame = { from, fromChessCom, updateHash, createHash };
